'use client'

import classnames from 'classnames'
import {useRouter} from 'next/navigation'
import {useId, useMemo, useState} from 'react'
import {useShowContactSales} from '~/app/(legacy)/contact/sales/hooks'
import MWDSButtonLink from '~/mercuryWebCompat/design-system/MWDSButtonLink'
import {externalLinks} from '~/routing/external-links'
import {internalLinks} from '~/routing/internal-links'
import {Analytics} from '~/utils/Analytics'
import styles from './styles.module.css'

type EmailAutofillProps = {
  classNames?: {
    /** CSS class that wraps the entire component */
    container?: string
    /** CSS class for  outermost div that wraps the HTML form */
    formWrapper?: string
    /** CSS class for the HTML input */
    input?: string
    /** CSS class for the button group container */
    buttonsContainer?: string
    /** CSS class that gets passed to the CTA MWDSButton wrapperClassName */
    ctaButtonWrapper?: string
    /** CSS class that gets passed to the CTA MWDSButton className */
    ctaButton?: string
  }
  ctaText?: string
  ctaLink?: string
  contactSalesLink?: string
  hideContactSales?: boolean
}

function EmailAutofill({
  classNames = {},
  ctaLink,
  ctaText = 'Open Account',
  contactSalesLink = internalLinks.contactSales,
  hideContactSales,
}: EmailAutofillProps) {
  const router = useRouter()
  const {data: showContactSales} = useShowContactSales()

  const extraClassNames = classNames
  const [email, setEmail] = useState('')

  const signupUrl = useMemo(() => {
    if (!email) {
      return ctaLink ?? externalLinks.auth.signup
    }

    return ctaLink ?? externalLinks.auth.autofillRedirect
  }, [ctaLink, email])
  const signupUrlWithEmail = useMemo(() => {
    const params = new URLSearchParams()

    if (!ctaLink) params.set('path', 'signup')
    params.set('email', email)

    return signupUrl + '?' + params.toString()
  }, [ctaLink, email, signupUrl])

  function trackRockerBox() {
    // Send RockerBox track event, but we don't ever want to block the form submission
    try {
      const windowWithRB = window as typeof window & {
        RB?: {
          track: (eventName: string, properties: Record<string, unknown>) => void
        }
      }
      windowWithRB.RB?.track('identify', {
        email,
      })
    } catch {
      // oh well, do nothing
    }
  }

  const onEmailChange = (value: string) => {
    setEmail(value)
  }

  const id = useId()

  /* the behavior for this form is atypical.
      rather than show user facing errors and block submission until valid input is submitted,
      we instead favor non-blocking and pass validation efforts to the forms on the following pages.
  */
  return (
    <div className={classnames(styles.emailContainer, extraClassNames?.container)}>
      <div className={classnames(styles.formWrapper, extraClassNames?.formWrapper)}>
        <div className={styles.inputWrapper}>
          <form
            onSubmit={e => {
              e.preventDefault()
            }}
          >
            <label htmlFor={id} className={styles.visuallyHidden}>
              Enter your email
            </label>
            <input
              id={id}
              className={classnames(extraClassNames?.input)}
              onChange={event => {
                onEmailChange(event.target.value)
              }}
              value={email}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  trackRockerBox()
                  router.push(signupUrlWithEmail)
                  Analytics.trackClick({
                    type: 'signup',
                    location: 'hero',
                    text: 'Get Started',
                    linkedPath: signupUrl,
                    customProperties: {
                      enterKeypress: 'true',
                      emailPrepopulated: email ? 'true' : 'false',
                    },
                  })
                }
              }}
              placeholder="Enter your email"
              type="email"
            />
          </form>
        </div>
      </div>
      <div
        className={classnames(
          styles.buttonContainer,
          extraClassNames?.buttonsContainer
        )}
      >
        <MWDSButtonLink
          variant="primary"
          size="big"
          href={signupUrlWithEmail}
          wrapperClassName={classNames.ctaButtonWrapper}
          className={classNames.ctaButton}
          onClick={trackRockerBox}
          analyticsEvent={{
            type: 'signup',
            location: 'hero',
            text: ctaText,
            linkedPath: signupUrl,
            customProperties: {
              emailPrepopulated: email ? 'true' : 'false',
            },
          }}
        >
          {ctaText}
        </MWDSButtonLink>
        {!!showContactSales?.pageIsVisible && !hideContactSales && (
          <MWDSButtonLink
            prefetch={!email}
            size="big"
            variant="secondary"
            href={{
              pathname: contactSalesLink,
              query: email ? {email} : null,
            }}
            analyticsEvent={{
              type: 'contact-sales',
              location: 'hero',
              text: 'Contact Sales',
              linkedPath: contactSalesLink,
              customProperties: {
                emailPrepopulated: email ? 'true' : 'false',
              },
            }}
          >
            Contact Sales
          </MWDSButtonLink>
        )}
      </div>
    </div>
  )
}

export default EmailAutofill
